/**
 * Generate calendar days for a month view
 * @param {Date} date - Date object representing the month to display
 * @returns {Array} Array of day objects for the calendar
 */
export const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDayOfMonth = new Date(year, month, 1);
    // Last day of the month
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // Day of the week for the first day (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = firstDayOfMonth.getDay();
    
    // Total days in the month
    const daysInMonth = lastDayOfMonth.getDate();
    
    // Calculate days needed from previous month
    const daysFromPrevMonth = firstDayOfWeek;
    
    // Get total days needed in the calendar grid (always display 6 weeks)
    const totalDays = 42; // 6 weeks × 7 days
    
    const calendarDays = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Add days from previous month
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    
    for (let i = daysInPrevMonth - daysFromPrevMonth + 1; i <= daysInPrevMonth; i++) {
        const dayDate = new Date(year, month - 1, i);
        calendarDays.push({
            date: dayDate.toISOString().split('T')[0],
            dayOfMonth: i,
            isCurrentMonth: false,
            isToday: dayDate.getTime() === today.getTime(),
        });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
        const dayDate = new Date(year, month, i);
        calendarDays.push({
            date: dayDate.toISOString().split('T')[0],
            dayOfMonth: i,
            isCurrentMonth: true,
            isToday: dayDate.getTime() === today.getTime(),
        });
    }
    
    // Add days from next month to fill the grid
    const remainingDays = totalDays - calendarDays.length;
    for (let i = 1; i <= remainingDays; i++) {
        const dayDate = new Date(year, month + 1, i);
        calendarDays.push({
            date: dayDate.toISOString().split('T')[0],
            dayOfMonth: i,
            isCurrentMonth: false,
            isToday: dayDate.getTime() === today.getTime(),
        });
    }
    
    return calendarDays;
};

/**
 * Format activities data into a structure for the calendar
 * @param {Array} activities - Raw activities data from API
 * @param {Array} calendarDays - Calendar days array
 * @returns {Array} Updated calendar days with activities
 */
export const mapActivitiesToCalendar = (activities, calendarDays) => {
    console.log('🗓️ Mapping activities to calendar days');
    console.log('📊 Activities data:', activities);
    
    if (!activities || !Array.isArray(activities) || activities.length === 0) {
        console.warn('⚠️ No activity data to map to calendar');
        return calendarDays;
    }
    
    // Create a map of activities by date
    const activityByDate = {};
      activities.forEach(activity => {
        if (!activity) {
            console.warn('⚠️ Undefined or null activity found');
            return;
        }
        
        if (!activity.dateCompleted) {
            console.warn('⚠️ Activity missing dateCompleted field:', activity);
            return;
        }
        
        // Handle different date formats
        let datePart;
        try {
            // Handle if dateCompleted is already a string or a date object
            datePart = typeof activity.dateCompleted === 'string' 
                ? activity.dateCompleted.split('T')[0] 
                : new Date(activity.dateCompleted).toISOString().split('T')[0];
        } catch (error) {
            console.error('❌ Error parsing date from activity:', activity, error);
            return;
        }
        
        if (!activityByDate[datePart]) {
            activityByDate[datePart] = {};
        }
        
        if (!activityByDate[datePart][activity.itemType]) {
            activityByDate[datePart][activity.itemType] = 0;
        }
        
        activityByDate[datePart][activity.itemType] += activity.count || 1;
    });
    
    console.log('📅 Activity map by date created:', activityByDate);
    
    // Map activities to calendar days
    return calendarDays.map(day => {
        return {
            ...day,
            activities: activityByDate[day.date] || {}
        };
    });
};

/**
 * Calculate summary statistics from activity data
 * @param {Array} activities - Raw activities data from API
 * @returns {Object} Summary statistics
 */
export const calculateActivityStats = (activities) => {
    console.log('📊 Calculating activity statistics');
    
    if (!activities || !Array.isArray(activities) || activities.length === 0) {
        console.warn('⚠️ No activity data for statistics calculation');
        return {
            totalActivities: 0,
            totalDays: 0,
            byType: {}
        };
    }
    
    // Get unique dates
    const uniqueDates = new Set();
    
    // Track counts by type
    const byType = {};
    
    // Calculate total count
    let totalCount = 0;
    
    activities.forEach(activity => {
        if (!activity) {
            console.warn('⚠️ Undefined or null activity found in stats calculation');
            return;
        }
        
        if (!activity.dateCompleted) {
            console.warn('⚠️ Activity missing dateCompleted field for stats:', activity);
            return;
        }
        
        let date;
        try {
            // Handle if dateCompleted is already a string or a date object
            date = typeof activity.dateCompleted === 'string' 
                ? activity.dateCompleted.split('T')[0] 
                : new Date(activity.dateCompleted).toISOString().split('T')[0];
            uniqueDates.add(date);
        } catch (error) {
            console.error('❌ Error parsing date for stats:', error, activity);
            return;
        }
        
        if (!activity.itemType) {
            console.warn('⚠️ Activity missing itemType field:', activity);
            activity.itemType = 'unknown';
        }
          if (!byType[activity.itemType]) {
            byType[activity.itemType] = 0;
        }
        
        const count = activity.count || 1;
        byType[activity.itemType] += count;
        totalCount += count;
    });
    
    const stats = {
        totalActivities: totalCount,
        totalDays: uniqueDates.size,
        byType
    };
    
    console.log('📈 Calculated activity stats:', stats);
    return stats;
};
